import React, { useState } from 'react'
import useEditableName from '@Utils/UseEditableName'
import classnames from 'classnames'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import Return from '@Images/languages_view_return.svg'
import More from '@Images/workspace_view_more.svg'

import {
  IWorkspace,
} from '@SiteModules/Workspace/types'

import './WorkspaceViewHeader.scss'

interface WorkspaceViewHeaderProps {
  readonly fullWorkspaceView: boolean
  readonly workspace: IWorkspace | undefined
  readonly numberOfWorkspaceUsers: number
  readonly saving: boolean
  readonly hasRenamedWorkspaceSuccessfully: boolean
  resetOpenedWorkspace(): void
  handleRenameWorkspace(name: string): void
  openDeleteWorkspacePopup(): void
}

const WorkspaceViewHeader: React.FC<WorkspaceViewHeaderProps> = props => {
  const {
    workspace,
    fullWorkspaceView,
    saving,
    hasRenamedWorkspaceSuccessfully,
    numberOfWorkspaceUsers,
  } = props

  const [showDropdown, setShowDropdown] = useState(false)
  const workspaceName = workspace?.attributes.name || ''

  const {
    editing,
    editedName,
    inputWidth,
    nameContainerRef,
    inputRef,
    startEditing,
    changeName,
  } = useEditableName({
    name: workspaceName,
    saving,
    hasRenamedSuccessfully: hasRenamedWorkspaceSuccessfully,
    handleRename: props.handleRenameWorkspace,
    inputClassName: 'WorkspaceViewHeader__workspace-name-input'
  })

  useClickOutsideListener('WorkspaceViewHeader__workspace-more-wrapper', setShowDropdown, showDropdown)

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown)
  }

  const handleOpenDeleteWorkspacePopup = () => {
    setShowDropdown(false)
    props.openDeleteWorkspacePopup()
  }

  const renderWorkspaceName = () => {
    return (
      <div className="WorkspaceViewHeader__workspace-name-outer-wrapper">
        <div className="WorkspaceViewHeader__workspace-name-inner-wrapper">
          {fullWorkspaceView && (
            <Return
              className="WorkspaceViewHeader__return"
              onClick={props.resetOpenedWorkspace}
            />
          )}
          {editing ? (
            <input
              ref={inputRef}
              className="WorkspaceViewHeader__workspace-name-input"
              style={{ width: inputWidth }}
              value={editedName}
              onChange={changeName}
              autoFocus
            />
          ) : (
            <div
              className="WorkspaceViewHeader__workspace-name"
              onClick={startEditing}
              ref={nameContainerRef}
            >
              {workspaceName}
            </div>
          )}
        </div>
        <div className="WorkspaceViewHeader__workspace-more-wrapper">
          <More
            className="WorkspaceViewHeader__workspace-more"
            onClick={toggleDropdown}
          />
          {showDropdown && renderActionDropdown()}
        </div>
      </div>
    )
  }

  const renderInformation = () => {
    return (
      <div className="WorkspaceViewHeader__information">
        <div className="WorkspaceViewHeader__information-wrapper">
          <div className="WorkspaceViewHeader__information-label">
            Members
          </div>
          <div className="WorkspaceViewHeader__information-content">
            {numberOfWorkspaceUsers}
          </div>
        </div>
        <div className="WorkspaceViewHeader__information-wrapper">
          <div className="WorkspaceViewHeader__information-label">
            Projects
          </div>
          <div className="WorkspaceViewHeader__information-content">
            {workspace ? workspace.attributes.numberOfProjects : 0}
          </div>
        </div>
        <div className="WorkspaceViewHeader__information-wrapper">
          <div className="WorkspaceViewHeader__information-label">
            Created at
          </div>
          <div className="WorkspaceViewHeader__information-content">
            {workspace ? workspace.attributes.createdAt : ''}
          </div>
        </div>
      </div>
    )
  }

  const actionDropdownList = (
    <ul>
      <li
        className="WorkspaceViewHeader__action-dropdown-item"
        onClick={handleOpenDeleteWorkspacePopup}
      >
        <div className="WorkspaceViewHeader__action-dropdown-item-text">
          Remove workspace
        </div>
      </li>
    </ul>
  )

  const renderActionDropdown = () => {
    return (
      <div
        className="WorkspaceViewHeader__action-dropdown WorkspaceViewHeader__action-dropdown-element"
      >
        {actionDropdownList}
      </div>
    )
  }

  return (
    <div
      className={classnames(
        'WorkspaceViewHeader',
        { 'WorkspaceViewHeader__full': fullWorkspaceView}
      )}
    >
      {renderWorkspaceName()}
      {renderInformation()}
    </div>
  )
}

export default WorkspaceViewHeader
